import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { useSearchIndex, SearchResult } from '@/hooks/useSearchIndex';
import { 
  Database, 
  Code2, 
  List, 
  FileType, 
  Loader2,
  ArrowRight,
  CheckCircle2,
  Circle,
  Book,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface CommandPaletteProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function CommandPalette({ open: controlledOpen, onOpenChange }: CommandPaletteProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const { searchGrouped, isLoading } = useSearchIndex();
  
  const open = controlledOpen ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;

  // Reset query when dialog closes
  useEffect(() => {
    if (!open) {
      setQuery('');
    }
  }, [open]);

  // Handle keyboard shortcut
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || e.key === '/') {
        if (
          (e.target instanceof HTMLElement) && 
          (e.target.isContentEditable || 
           e.target.tagName === 'INPUT' || 
           e.target.tagName === 'TEXTAREA')
        ) {
          return;
        }
        e.preventDefault();
        setOpen(!open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [open, setOpen]);

  const results = searchGrouped(query);
  const hasResults = 
    results.standards.length > 0 || 
    results.fields.length > 0 || 
    results.enums.length > 0 || 
    results.dataTypes.length > 0 ||
    results.guides.length > 0;

  const handleSelect = useCallback((result: SearchResult) => {
    // Build the navigation path first
    let path = '';
    
    switch (result.type) {
      case 'standard':
        if (result.standard) {
          path = `/api-standards?schema=${result.standard.slug}`;
        }
        break;
      case 'field':
        if (result.field) {
          path = `/api-standards?schema=${result.field.standardSlug}&field=${result.field.fieldName}`;
        }
        break;
      case 'enum':
        if (result.enumValue) {
          // Navigate to first field that uses this enum
          if (result.enumValue.usedInFields.length > 0) {
            const firstUse = result.enumValue.usedInFields[0];
            path = `/api-standards?schema=${firstUse.standardSlug}&field=${firstUse.fieldName}`;
          }
        }
        break;
      case 'dataType':
        if (result.dataType) {
          path = `/docs/data-types?type=${result.dataType.slug}`;
        }
        break;
      case 'guide':
        if (result.guide) {
          path = `/docs/guides/${result.guide.slug}`;
        }
        break;
    }
    
    // Clear query immediately
    setQuery('');
    
    // Close dialog first, then navigate after a micro-delay to ensure clean state
    setOpen(false);
    if (path) {
      setTimeout(() => navigate(path), 0);
    }
  }, [navigate, setOpen]);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <Command className="rounded-lg border shadow-md" shouldFilter={false}>
        <CommandInput 
          placeholder="Search standards, fields, enum values..." 
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          {isLoading && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
          
          {!isLoading && query.length < 2 && (
            <CommandEmpty>
              <div className="text-center text-muted-foreground">
                <p>Type at least 2 characters to search</p>
                <p className="text-xs mt-1">Search across standards, fields, enum values & data types</p>
              </div>
            </CommandEmpty>
          )}
          
          {!isLoading && query.length >= 2 && !hasResults && (
            <CommandEmpty>No results found for "{query}"</CommandEmpty>
          )}

          {/* Standards Group */}
          {results.standards.length > 0 && (
            <>
              <CommandGroup heading="Standards">
                {results.standards.map((result) => (
                  <CommandItem
                    key={`standard-${result.standard?.slug}`}
                    value={`standard-${result.standard?.slug}`}
                    onSelect={() => handleSelect(result)}
                    className="flex items-center gap-3 py-3"
                  >
                    <Database className="h-4 w-4 text-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{result.standard?.title}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {result.standard?.description}
                      </div>
                    </div>
                    <Badge variant="secondary" className="shrink-0 text-xs">
                      {result.standard?.categoryName}
                    </Badge>
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
            </>
          )}

          {/* Fields Group */}
          {results.fields.length > 0 && (
            <>
              <CommandGroup heading="Fields">
                {results.fields.map((result, idx) => (
                  <CommandItem
                    key={`field-${result.field?.standardSlug}-${result.field?.fieldName}-${idx}`}
                    value={`field-${result.field?.standardSlug}-${result.field?.fieldName}`}
                    onSelect={() => handleSelect(result)}
                    className="flex items-center gap-3 py-3"
                  >
                    <Code2 className="h-4 w-4 text-blue-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <code className="font-mono text-sm">{result.field?.fieldName}</code>
                        {result.field?.isRequired ? (
                          <CheckCircle2 className="h-3 w-3 text-amber-500" />
                        ) : (
                          <Circle className="h-3 w-3 text-muted-foreground/50" />
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        in {result.field?.standardTitle}
                        {result.field?.usedInStandards && result.field.usedInStandards.length > 0 && (
                          <span className="text-primary">
                            {' • '}Also in: {result.field.usedInStandards.slice(0, 2).map(s => s.title).join(', ')}
                            {result.field.usedInStandards.length > 2 && ` +${result.field.usedInStandards.length - 2}`}
                          </span>
                        )}
                      </div>
                    </div>
                    <Badge variant="outline" className="shrink-0 text-xs font-mono">
                      {result.field?.fieldType}
                    </Badge>
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
            </>
          )}

          {/* Enum Values Group */}
          {results.enums.length > 0 && (
            <>
              <CommandGroup heading="Enum Values">
                {results.enums.map((result, idx) => (
                  <CommandItem
                    key={`enum-${result.enumValue?.value}-${idx}`}
                    value={`enum-${result.enumValue?.value}`}
                    onSelect={() => handleSelect(result)}
                    className="flex items-center gap-3 py-3"
                  >
                    <List className="h-4 w-4 text-green-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <code className="font-mono text-sm">{result.enumValue?.value}</code>
                        <ArrowRight className="h-3 w-3 text-muted-foreground" />
                        <span className="text-sm">{result.enumValue?.displayName}</span>
                      </div>
                      {result.enumValue?.usedInFields && result.enumValue.usedInFields.length > 0 && (
                        <div className="text-xs text-muted-foreground truncate">
                          Used in: {result.enumValue.usedInFields.slice(0, 2).map(f => f.fieldName).join(', ')}
                          {result.enumValue.usedInFields.length > 2 && ` +${result.enumValue.usedInFields.length - 2}`}
                        </div>
                      )}
                    </div>
                    <Badge variant="secondary" className="shrink-0 text-xs">
                      {result.enumValue?.lookupGroup}
                    </Badge>
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
            </>
          )}

          {/* Data Types Group */}
          {results.dataTypes.length > 0 && (
            <CommandGroup heading="Data Types">
              {results.dataTypes.map((result) => (
                <CommandItem
                  key={`datatype-${result.dataType?.slug}`}
                  value={`datatype-${result.dataType?.slug}`}
                  onSelect={() => handleSelect(result)}
                  className="flex items-center gap-3 py-3"
                >
                  <FileType className="h-4 w-4 text-purple-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{result.dataType?.name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {result.dataType?.shortDescription}
                    </div>
                  </div>
                  {result.dataType?.exampleValue && (
                    <code className="text-xs text-muted-foreground font-mono shrink-0">
                      {result.dataType.exampleValue.slice(0, 20)}
                      {result.dataType.exampleValue.length > 20 && '...'}
                    </code>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {/* Guides Group */}
          {results.guides.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Guides">
                {results.guides.map((result) => (
                  <CommandItem
                    key={`guide-${result.guide?.slug}`}
                    value={`guide-${result.guide?.slug}`}
                    onSelect={() => handleSelect(result)}
                    className="flex items-center gap-3 py-3"
                  >
                    <Book className="h-4 w-4 text-blue-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{result.guide?.title}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {result.guide?.excerpt}
                      </div>
                    </div>
                    <Badge variant="secondary" className="shrink-0 text-xs">
                      {result.guide?.categoryName}
                    </Badge>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}
        </CommandList>
        
        {/* Footer with keyboard hints */}
        <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium">
                ↑↓
              </kbd>
              Navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium">
                ↵
              </kbd>
              Select
            </span>
          </div>
          <span className="flex items-center gap-1">
            <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium">
              esc
            </kbd>
            Close
          </span>
        </div>
      </Command>
    </CommandDialog>
  );
}
